/**
 * Sentry RUM — single source of truth for org/project identifiers; no inline
 * magic strings for the org or project name.
 *
 * DSN is intentionally designed to be embedded in client bundles by Sentry —
 * it is not a secret, but it IS environment-specific (different DSN per Sentry
 * platform: backend vs. frontend, even within the same Sentry org).
 *
 * Keep this module free of browser and SDK imports: vite.config.ts (Node, no DOM lib) imports it
 * for the org and project constants. Event filters live in sentryFilters.ts,
 * SDK options in sentryInit.ts, the init call in sentryBootstrap.ts.
 */

export const SENTRY_ORG = 'beekod';
export const SENTRY_PROJECT = 'beetegre-v2';

/**
 * Build a Sentry user identity string for triage context without PII.
 * The backend LoginResponse does not expose a user UUID in the body (it is
 * encoded inside the JWT claim but not surfaced to the client intentionally).
 * tenantId alone is sufficient to correlate error spikes to a specific tenant.
 * Format: "tenant:<tenantId>" for tenant users; "superadmin" for SuperAdmin.
 */
export function buildSentryUserId(tenantId: string | null, role: string): string {
  if (role === 'SuperAdmin') return 'superadmin';
  return `tenant:${tenantId ?? 'none'}`;
}

/** Non-prod deploy hosts, used when the build passes no `VITE_SENTRY_ENVIRONMENT`. */
export const SENTRY_ENVIRONMENT_BY_HOST: ReadonlyMap<string, string> = new Map([
  ['dev.beetegre.com', 'development'],
]);

/**
 * Sentry `environment` is the deploy target, not the Vite build mode: the dev deploy is also a
 * production build (`MODE === 'production'`), so MODE alone files dev events under prod (AB#2470).
 * Order: explicit `VITE_SENTRY_ENVIRONMENT`, then the known deploy host, then MODE, so prod
 * filters and alerts keep matching on unknown hosts. Lowercased because Sentry environments are
 * case-sensitive and the frontend has always reported `production`.
 */
export function resolveSentryEnvironment(
  configured: string | undefined,
  mode: string,
  hostname?: string,
): string {
  const normalized = configured?.trim().toLowerCase();
  if (normalized) return normalized;
  const host = hostname?.toLowerCase().replace(/\.$/, '');
  return (host && SENTRY_ENVIRONMENT_BY_HOST.get(host)) || mode;
}
